import React, { useEffect, useState } from "react";
import { FaLinkedinIn } from "react-icons/fa";
import { SERVICE_ROUTES } from "../constants/serviceRoutes";

const FOOTER_BREADCRUMB_PATH_KEY = "blackRothFooterBreadcrumbPath";

const XSocialIcon = ({ size = 18 }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
  >
    <path d="M18.901 1.153h3.68l-8.04 9.188L24 22.847h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932zM17.61 20.645h2.039L6.486 3.24H4.298z" />
  </svg>
);

const Footer = () => {
  const [footerBreadcrumbPath] = useState(() => {
    const path = window.sessionStorage.getItem(FOOTER_BREADCRUMB_PATH_KEY);
    window.sessionStorage.removeItem(FOOTER_BREADCRUMB_PATH_KEY);
    return path;
  });

  const getViewportMode = () => {
    if (
      window.innerWidth >= 760 &&
      window.innerWidth <= 785 &&
      window.innerHeight >= 1000 &&
      window.innerHeight <= 1050
    ) {
      return "tablet-768";
    }

    if (
      window.innerWidth >= 800 &&
      window.innerWidth <= 860 &&
      window.innerHeight >= 1140 &&
      window.innerHeight <= 1225
    ) {
      return "tablet-820";
    }

    if (
      window.innerWidth >= 1000 &&
      window.innerWidth <= 1060 &&
      window.innerHeight >= 1320 &&
      window.innerHeight <= 1405
    ) {
      return "tablet-1024";
    }

    if (window.innerWidth < 600) {
      return "mobile";
    }

    if (window.innerWidth < 768) {
      return "tablet-small";
    }

    if (window.innerWidth < 1024) {
      return "tablet-portrait";
    }

    if (window.innerWidth < 1280) {
      return "tablet-landscape";
    }

    return "desktop";
  };

  const [viewportMode, setViewportMode] = useState(getViewportMode());

  useEffect(() => {
    const handleResize = () => setViewportMode(getViewportMode());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const footerOnlyRoutes = new Set([
      SERVICE_ROUTES.dataScienceAi,
      SERVICE_ROUTES.objectDetection,
      SERVICE_ROUTES.edgeComputing,
      SERVICE_ROUTES.iotIntegration,
      SERVICE_ROUTES.cloudSecurity,
      SERVICE_ROUTES.dataVisualization,
      SERVICE_ROUTES.uiUxDesign,
      SERVICE_ROUTES.mobileDevelopment,
      SERVICE_ROUTES.aiMlServices,
      SERVICE_ROUTES.dataScience,
      SERVICE_ROUTES.payrollServices,
      SERVICE_ROUTES.payrollSoftware,
      SERVICE_ROUTES.payrollProcessing,
      SERVICE_ROUTES.bpoServices,
      SERVICE_ROUTES.contentModeration,
      SERVICE_ROUTES.customerSupport,
      SERVICE_ROUTES.hrPayrollOperations,
      SERVICE_ROUTES.recruitmentProcessOutsourcing,
      SERVICE_ROUTES.backOfficeOperations,
      SERVICE_ROUTES.dataEntryServices,
      SERVICE_ROUTES.technicalSupport,
      SERVICE_ROUTES.medicalCoding,
      SERVICE_ROUTES.medicalBilling,
      SERVICE_ROUTES.revenueCycleManagement,
      SERVICE_ROUTES.claimsManagement,
      SERVICE_ROUTES.insuranceEligibilityVerification,

    ]);
    const payrollFooterRoutes = new Set([
      SERVICE_ROUTES.payrollServices,
      SERVICE_ROUTES.payrollSoftware,
      SERVICE_ROUTES.payrollProcessing,
    ]);

    const shouldHideBreadcrumbs =
      footerBreadcrumbPath === window.location.pathname &&
      footerOnlyRoutes.has(window.location.pathname);
    const shouldSendBackToHome =
      footerBreadcrumbPath === window.location.pathname &&
      payrollFooterRoutes.has(window.location.pathname);

    document.querySelectorAll(".breadcrumbs").forEach((breadcrumb) => {
      if (shouldHideBreadcrumbs) {
        breadcrumb.dataset.footerHiddenBreadcrumb = "true";
        breadcrumb.style.display = "none";
      } else if (breadcrumb.dataset.footerHiddenBreadcrumb === "true") {
        breadcrumb.style.display = "";
        delete breadcrumb.dataset.footerHiddenBreadcrumb;
      }
    });

    const handlePayrollServicesCardClick = (event) => {
      if (window.location.pathname !== SERVICE_ROUTES.payrollServices) {
        return;
      }

      const serviceCard = event.target.closest?.(".service-card");

      if (!serviceCard) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }

      window.location.assign("about:blank");
    };

    window.addEventListener("click", handlePayrollServicesCardClick, true);

    const backLink = document.querySelector(".standard-back-link a");
    let handleBackToHomeClick;

    if (shouldSendBackToHome && backLink) {
      backLink.setAttribute("href", "/");

      handleBackToHomeClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.location.href = "/";
      };

      backLink.addEventListener("click", handleBackToHomeClick, true);
    }

    return () => {
      window.removeEventListener("click", handlePayrollServicesCardClick, true);

      if (backLink && handleBackToHomeClick) {
        backLink.removeEventListener("click", handleBackToHomeClick, true);
      }
    };
  }, [footerBreadcrumbPath]);

  const isMobile = viewportMode === "mobile";
  const isTablet768 = viewportMode === "tablet-768";
  const isTabletSmall = viewportMode === "tablet-small";
  const isTablet820 = viewportMode === "tablet-820";
  const isTablet1024 = viewportMode === "tablet-1024";
  const isTabletPortrait = viewportMode === "tablet-portrait";
  const isTabletLandscape = viewportMode === "tablet-landscape";

  const footerGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : isTablet768 || isTablet820 || isTablet1024 || isTabletSmall || isTabletPortrait
        ? "repeat(2, minmax(0, 1fr))"
        : isTabletLandscape
          ? "repeat(3, minmax(0, 1fr))"
          : "repeat(7, minmax(0, 1fr))",
    columnGap: isMobile
      ? "18px"
      : isTablet768
        ? "34px"
        : isTablet820 || isTablet1024 || isTabletPortrait
          ? "22px"
          : isTabletSmall
            ? "20px"
            : "30px",
    rowGap: isMobile
      ? "20px"
      : isTablet768
        ? "0px"
        : isTablet820 || isTablet1024 || isTabletSmall || isTabletPortrait
          ? "20px"
          : "30px",
    maxWidth: "1200px",
    margin: "auto",
    width: "100%",
    flex: 1,
    alignContent: isTablet768 ? "space-between" : "start",
  };

  const footerShellStyle = {
    background: "#0a0a0a",
    color: "#ccc",
    width: "100%",
    boxSizing: "border-box",
    padding: isMobile
      ? "22px 16px 18px"
      : isTablet768
        ? "14px 14px 12px"
      : isTablet820
        ? "26px 18px 18px"
        : isTablet1024
          ? "28px 20px 18px"
          : isTabletSmall
            ? "24px 18px 18px"
            : isTabletPortrait
              ? "26px 18px 18px"
              : isTabletLandscape
                ? "32px 22px 20px"
                : "16px 20px 20px",
    height: isTablet768 ? "100vh" : "auto",
    minHeight: isTablet768 ? "100vh" : "auto",
    marginTop: isTablet768 ? "-18px" : "0",
    display: "flex",
    flexDirection: "column",
    justifyContent: isTablet768 ? "space-between" : "flex-start",
    position: "relative",
    zIndex: 2,
    overflow: "hidden",
  };

  const footerColumnStyle = {
    minWidth: 0,
  };

  const footerTitleStyle = {
    color: "#fff",
    fontSize: isMobile || isTabletSmall ? "14px" : isTablet768 ? "12px" : isTablet820 || isTablet1024 || isTabletPortrait || isTabletLandscape ? "15px" : "14px",
    marginBottom: isTablet768 ? "6px" : "10px",
    textAlign: isTablet820 || isTablet1024 || isTabletPortrait ? "left" : "left",
  };

  const footerLinkStyle = {
    color: "#ccc",
    fontSize: isMobile ? "14px" : isTablet768 ? "11px" : isTablet820 ? "13px" : isTablet1024 ? "13px" : isTabletPortrait ? "13px" : "14px",
    textDecoration: "none",
    cursor: "pointer",
    textAlign: "left",
    lineHeight: isTablet768 ? "1.15" : "1.25",
  };

  const socialRowStyle = {
    marginTop: isMobile ? "28px" : isTablet768 ? "12px" : isTablet820 ? "24px" : isTablet1024 ? "24px" : isTabletSmall ? "26px" : isTabletPortrait ? "24px" : "32px",
    marginBottom: isMobile ? "-10px" : isTablet768 ? "-2px" : isTablet820 ? "-8px" : isTablet1024 ? "-10px" : isTabletSmall ? "-8px" : isTabletPortrait ? "-8px" : "-18px",
    display: "flex",
    justifyContent: "center",
    gap: isMobile ? "18px" : isTablet768 ? "14px" : isTablet820 ? "18px" : isTablet1024 ? "18px" : isTabletSmall ? "18px" : isTabletPortrait ? "18px" : "25px",
    flexWrap: "wrap",
  };

  const bottomStyle = {
    borderTop: "1px solid #222",
    marginTop: isMobile ? "22px" : isTablet768 ? "10px" : isTablet820 ? "18px" : isTablet1024 ? "18px" : isTabletSmall ? "20px" : isTabletPortrait ? "20px" : "30px",
    paddingTop: isMobile ? "16px" : isTablet768 ? "8px" : isTablet820 ? "12px" : isTablet1024 ? "12px" : isTabletSmall ? "14px" : isTabletPortrait ? "14px" : "20px",
    textAlign: "center",
    fontSize: isMobile ? "13px" : isTablet768 ? "12px" : "14px",
  };

  const handleFooterServiceClick = (href) => {
    window.sessionStorage.setItem(FOOTER_BREADCRUMB_PATH_KEY, href);
  };

  const footerColumns = [
    {
      title: "BlackRoth",
      links: [
        { text: "Contact Us", href: "/contact-us"},
        { text: "About Us", href: "/about-us" },
        { text: "Careers", href: "/careers" },
        { text: "Newsroom", href: "/newsroom" },
        { text: "Fraud Alert", href: "/fraud-alert" },
        { text: "Portfolio",
          href: "https://main.dg6gq0suatkji.amplifyapp.com/",
          target: "_blank",
          rel: "noopener noreferrer",
        },
        
      ],
    },
    {
      title: "Our Services",
      links: [
        { text: "Data science with AI", href: SERVICE_ROUTES.dataScienceAi, hideBreadcrumbs: true },
        { text: "Object Detection", href: SERVICE_ROUTES.objectDetection, hideBreadcrumbs: true },
        { text: "Edge Computing", href: SERVICE_ROUTES.edgeComputing, hideBreadcrumbs: true },
        { text: "IoT Integration", href: SERVICE_ROUTES.iotIntegration, hideBreadcrumbs: true },
        { text: "Cloud Security", href: SERVICE_ROUTES.cloudSecurity, hideBreadcrumbs: true },
        { text: "Data Visualization", href: SERVICE_ROUTES.dataVisualization, hideBreadcrumbs: true },
        { text: "UI/UX Design", href: SERVICE_ROUTES.uiUxDesign, hideBreadcrumbs: true },
        { text: "Mobile Development", href: SERVICE_ROUTES.mobileDevelopment, hideBreadcrumbs: true },
        { text: "AI & ML", href: SERVICE_ROUTES.aiMlServices, hideBreadcrumbs: true },
        { text: "Data Science", href: SERVICE_ROUTES.dataScience, hideBreadcrumbs: true },
      ],
    },
    {
      title: "Payroll",
      links: [
        { text: "Payroll Services", href: SERVICE_ROUTES.payrollServices, hideBreadcrumbs: true },
        { text: "Payroll Software", href: SERVICE_ROUTES.payrollSoftware, hideBreadcrumbs: true },
        { text: "Payroll Processing", href: SERVICE_ROUTES.payrollProcessing, hideBreadcrumbs: true },
      ],
    },
    {
      title: "HR Services",
      links: [
        { text: "BPO Services", href: SERVICE_ROUTES.bpoServices, hideBreadcrumbs: true },
        { text: "Content Moderation", href: SERVICE_ROUTES.contentModeration, hideBreadcrumbs: true },
        { text: "Customer Support", href: SERVICE_ROUTES.customerSupport, hideBreadcrumbs: true },
        { text: "HR & Payroll Operations", href: SERVICE_ROUTES.hrPayrollOperations, hideBreadcrumbs: true },
        { text: "Recruitment Process Outsourcing", href: SERVICE_ROUTES.recruitmentProcessOutsourcing, hideBreadcrumbs: true },
        { text: "Back Office Operations", href: SERVICE_ROUTES.backOfficeOperations, hideBreadcrumbs: true },
        { text: "Data Entry Services", href: SERVICE_ROUTES.dataEntryServices, hideBreadcrumbs: true },
        { text: "Technical Support", href: SERVICE_ROUTES.technicalSupport, hideBreadcrumbs: true },
      ],
    },
    {
      title: "Health Services",
      links: [
        { text: "Medical Coding", href: SERVICE_ROUTES.medicalCoding, hideBreadcrumbs: true },
        { text: "Medical Billing", href: SERVICE_ROUTES.medicalBilling, hideBreadcrumbs: true },
        { text: " RCM", href: SERVICE_ROUTES.revenueCycleManagement, hideBreadcrumbs: true },
        { text: "Claims Management", href: SERVICE_ROUTES.claimsManagement, hideBreadcrumbs: true },
        { text: "Insurance Eligibility Verification", href: SERVICE_ROUTES.insuranceEligibilityVerification, hideBreadcrumbs: true },
      ],
    },
    {
      title: "Resources",
      links: [
        { text: "Blog", href: "/blog" },
        { text: "Help Center", href: "/help" },
        { text: "API Docs", href: "/apidocs" },
      ],
    },
    {
      title: "Legal",
      links: [
        { text: "Privacy Policy", href: "/privacy" },
        { text: "Terms of Service", href: "/terms" },
        { text: "Security", href: "/security" },
      ],
    },
  ];

  return (
    <footer style={footerShellStyle}>
      <div style={footerGridStyle}>
        {footerColumns.map((column, index) => (
          <div key={index} style={footerColumnStyle}>
            <h4 style={footerTitleStyle}>
              {column.title}
            </h4>

            <ul style={{ listStyle: "none", padding: 0 }}>
              {column.links.map((link, i) => (
                <li key={i} style={{ marginBottom: isMobile ? "5px" : "8px" }}>
                  <a
                    href={link.href}
                    target={link.target}
                    rel={link.rel}
                    onClick={link.hideBreadcrumbs ? () => handleFooterServiceClick(link.href) : undefined}
                    style={footerLinkStyle}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div style={socialRowStyle}>
        <span style={{ color: "#fff", fontSize: isMobile ? "13px" : "14px" }}>Follow Us</span>

        <a
          href="https://x.com/BlackRothPvtLtd"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit" }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <XSocialIcon size={18} />
          </span>
        </a>

        <a
          href="https://in.linkedin.com/company/blackroth"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit" }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <FaLinkedinIn size={18} aria-hidden="true" />
          </span>
        </a>
      </div>

      <div style={bottomStyle}>
        &copy; 2026 BlackRoth <span className="trademark">TM</span>  .  All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;
